import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories } from '@/mocks/comics';

function StatItem({ number, suffix, label }: { number: string; suffix: string; label: string }) {
  return (
    <div className="text-center px-4 first:pl-0 last:pr-0">
      <p className="text-3xl md:text-4xl font-bold text-accent-500">
        {number}<span className="text-xl">{suffix}</span>
      </p>
      <p className="mt-2 text-xs md:text-sm text-foreground-500">{label}</p>
    </div>
  );
}

export default function AboutPage() {
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormSuccess(true);
      setFormError('');
      return;
    }

    setSubmitting(true);
    setFormError('');

    try {
      const searchParams = new URLSearchParams();
      const email = formData.get('email') as string;
      const name = formData.get('name') as string;
      const message = formData.get('message') as string;
      if (email) searchParams.append('email', email);
      if (name) searchParams.append('name', name);
      if (message) searchParams.append('message', message);

      const response = await fetch('https://readdy.ai/api/form/d969qf5tfb6ioa7s1lqg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: searchParams.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore
      }

      if (response.ok && parsed.code === 'OK') {
        setFormSuccess(true);
        setFormError('');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后再试';
        setFormError(serverMsg);
        setFormSuccess(false);
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormSuccess(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="pt-1">
          <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />
        </div>

        <section className="px-4 md:px-6 py-10 md:py-16">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
              <div className="lg:w-[55%]">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-1.5 h-5 rounded-full bg-accent-500"></span>
                  <span className="text-sm text-foreground-500 font-medium">品牌理念</span>
                </div>
                <blockquote className="text-2xl md:text-3xl font-heading text-foreground-900 leading-relaxed">
                  &ldquo;让每一位漫画爱好者都能随时随地、
                  <br />
                  免费畅享最优质的漫画阅读体验。&rdquo;
                </blockquote>
                <div className="mt-6 flex items-center gap-3">
                  <span className="text-sm font-medium text-foreground-900">51漫画团队</span>
                  <span className="flex items-center gap-0.5 text-accent-500">
                    <i className="ri-star-fill text-sm"></i>
                    <i className="ri-star-fill text-sm"></i>
                    <i className="ri-star-fill text-sm"></i>
                    <i className="ri-star-fill text-sm"></i>
                    <i className="ri-star-fill text-sm"></i>
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded bg-accent-100 text-accent-700">品牌认证</span>
                </div>
              </div>
              <div className="lg:w-[40%] flex justify-center">
                <div className="w-48 h-48 md:w-56 md:h-56 rounded-2xl overflow-hidden bg-background-200">
                  <img
                    src="https://readdy.ai/api/search-image?query=Minimalist%20abstract%20illustration%20of%20open%20book%20with%20warm%20orange%20and%20cream%20gradient%2C%20clean%20simple%20design%2C%20modern%20digital%20art%2C%20soft%20lighting%2C%20editorial%20style&width=500&height=500&seq=about-brand-01&orientation=squarish"
                    alt="51漫画品牌形象"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
              <div className="flex-1 w-full">
                <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 leading-snug">
                  关于51漫画
                  <br />
                  我们用数据说话
                </h2>
                <div className="mt-10 grid grid-cols-3 divide-x divide-foreground-200/40">
                  <StatItem number="12,000" suffix="+" label="海量漫画资源" />
                  <StatItem number="500" suffix="万+" label="忠实读者" />
                  <StatItem number="99.9" suffix="%" label="服务稳定性" />
                </div>
              </div>
              <div className="flex-1 w-full flex justify-center">
                <div className="relative w-full max-w-md aspect-square bg-gradient-to-br from-accent-100/60 to-accent-200/40 rounded-2xl overflow-hidden">
                  <div className="absolute top-8 left-8 w-24 h-24 rounded-full bg-accent-300/40"></div>
                  <div className="absolute top-20 right-10 w-20 h-32 rounded-3xl bg-accent-400/30"></div>
                  <div className="absolute bottom-12 left-16 w-28 h-28 rounded-2xl bg-accent-200/50"></div>
                  <div className="absolute bottom-16 right-6 w-16 h-16 rounded-full bg-accent-500/25"></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6 mb-8 md:mb-10">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 leading-snug max-w-md">
                为什么选择
                <br />
                51漫画
              </h2>
              <p className="text-sm text-foreground-500 max-w-xs leading-relaxed">
                我们致力于打造最优质的漫画阅读平台，从内容到体验，每个细节都用心打磨。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              <div className="md:col-span-2 bg-background-100 rounded-lg p-5 md:p-6">
                <div className="w-full aspect-[16/9] rounded-md overflow-hidden mb-4">
                  <img
                    src="https://readdy.ai/api/search-image?query=Abstract%20warm%20tone%20illustration%20of%20digital%20reading%20experience%2C%20manhua%20panels%20floating%2C%20soft%20orange%20and%20cream%20gradient%2C%20minimalist%20clean%20design%2C%20modern%20digital%20art%2C%20editorial%20style&width=800&height=500&seq=about-feat-01&orientation=landscape"
                    alt="海量正版漫画资源"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <h3 className="text-base md:text-lg font-medium text-foreground-900">海量正版 · 每日更新</h3>
                <p className="mt-1.5 text-sm text-foreground-500 leading-relaxed">
                  收录超过12,000部漫画作品，涵盖{categories.length}大分类。每日同步更新热门连载，让你追更不等待。
                </p>
              </div>

              <div className="flex flex-col gap-4 md:gap-5">
                <div className="flex-1 bg-background-100 rounded-lg p-5 md:p-6 flex flex-col justify-center">
                  <h3 className="text-base md:text-lg font-medium text-foreground-900 text-center">高清画质</h3>
                  <div className="mt-4 flex justify-center">
                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-accent-100 flex items-center justify-center">
                      <i className="ri-hd-line text-2xl text-accent-500"></i>
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-foreground-500 text-center leading-relaxed">
                    全站高清漫画，画质细腻，给你沉浸式阅读体验。
                  </p>
                </div>
                <div className="flex-1 bg-background-100 rounded-lg p-5 md:p-6 flex flex-col justify-center">
                  <h3 className="text-base md:text-lg font-medium text-foreground-900 text-center">免费畅读</h3>
                  <div className="mt-4 flex justify-center">
                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-accent-100 flex items-center justify-center">
                      <i className="ri-gift-line text-2xl text-accent-500"></i>
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-foreground-500 text-center leading-relaxed">
                    所有漫画免费阅读，无需付费，让漫画回归分享的乐趣。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 text-center">
              读者口碑
            </h2>
            <p className="mt-3 text-sm text-foreground-500 text-center max-w-md mx-auto">
              来自真实读者的评价，是我们前进的最大动力
            </p>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {[
                { name: '漫画迷小王', text: '在51漫画追了三年了，更新快画质好，每次新章节都能第一时间看到，体验超棒！', rating: 5 },
                { name: '二次元少女', text: '分类很全，什么类型的漫画都能找到。界面简洁好看，阅读体验很流畅。', rating: 5 },
                { name: '追更达人', text: '每天打开51漫画已经成了习惯，平台一直在优化，功能越来越完善了。强烈推荐！', rating: 5 },
                { name: '资深读者', text: '用了很多漫画平台，51漫画是体验最好的。广告少、加载快、画质清晰，真心推荐。', rating: 5 },
                { name: '学生党小李', text: '免费又好看，对于学生党来说太友好了。希望51漫画越做越好，未来可期！', rating: 4 },
                { name: '上班族阿杰', text: '通勤路上必看51漫画，离线缓存功能很实用。画风和内容都很对我胃口。', rating: 5 },
              ].map((review, idx) => (
                <div key={idx} className="bg-background-50 rounded-lg p-5 border border-background-200/70">
                  <div className="flex items-center gap-1 text-accent-500 mb-3">
                    {Array.from({ length: review.rating }, (_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{review.text}</p>
                  <p className="mt-4 text-xs text-foreground-400 font-medium">—— {review.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading text-foreground-900">联系我们</h2>
                <p className="mt-3 text-sm text-foreground-500 leading-relaxed">
                  有任何问题或合作意向，欢迎随时联系我们。我们会在24小时内回复。
                </p>
                <div className="mt-8 space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-lg text-accent-500"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">电话</p>
                      <p className="text-sm text-foreground-500">400-888-5151</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-lg text-accent-500"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">邮箱</p>
                      <p className="text-sm text-foreground-500">contact@51comic.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-lg text-accent-500"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">地址</p>
                      <p className="text-sm text-foreground-500">北京市朝阳区望京SOHO T1座12层1206室</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="bg-background-100 rounded-lg p-6 md:p-8">
                  <h3 className="text-lg font-medium text-foreground-900">发送消息</h3>
                  <form
                    className="mt-6 space-y-4"
                    onSubmit={handleSubmit}
                    data-readdy-form=""
                  >
                    <div className="spam-shield">
                      <input
                        type="text"
                        name="website_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>
                    <div>
                      <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        id="about-name"
                        name="name"
                        type="text"
                        required
                        className="w-full h-10 px-4 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-accent-300 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        id="about-email"
                        name="email"
                        type="email"
                        required
                        className="w-full h-10 px-4 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-accent-300 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言</label>
                      <textarea
                        id="about-message"
                        name="message"
                        rows={4}
                        maxLength={500}
                        required
                        className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-accent-300 transition-colors resize-none"
                        placeholder="请输入您的留言内容（500字以内）"
                      ></textarea>
                    </div>
                    {formSuccess && (
                      <div className="p-3 rounded-lg bg-accent-100 text-accent-700 text-sm">
                        消息已发送成功！我们会尽快与您联系。
                      </div>
                    )}
                    {formError && (
                      <div className="p-3 rounded-lg bg-accent-100 text-accent-800 text-sm">
                        {formError}
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={submitting}
                      className="whitespace-nowrap w-full py-3 rounded-full bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors disabled:opacity-50 cursor-pointer"
                    >
                      {submitting ? '发送中...' : '发送消息'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 font-bold">
              与我们合作
            </h2>
            <p className="mt-3 text-sm text-foreground-500 max-w-md mx-auto">
              欢迎漫画作者、版权方、广告主与我们联系，共创漫画产业的未来。
            </p>
            <a
              href="#"
              className="whitespace-nowrap mt-6 inline-flex items-center gap-2 px-8 py-3 rounded-full bg-primary-500 text-background-50 text-sm md:text-base font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              商务合作 <i className="ri-arrow-right-line"></i>
            </a>
            <div className="mt-10">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20warm%20tone%20partnership%20collaboration%20horizontal%20banner%2C%20soft%20orange%20and%20cream%20gradient%2C%20minimalist%20clean%20design%2C%20modern%20digital%20art%2C%20editorial%20style%2C%20no%20text&width=1200&height=250&seq=about-cta-banner&orientation=landscape"
                alt="商务合作"
                className="w-full rounded-lg"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 text-center">
              常见问题
            </h2>
            <div className="mt-10 space-y-3">
              {[
                { q: '51漫画是免费的吗？', a: '是的，51漫画平台上的所有漫画作品均免费提供给读者在线阅读，无需付费。我们致力于让更多人享受漫画的乐趣。' },
                { q: '51漫画支持哪些设备？', a: '51漫画支持电脑、平板、手机等多种设备。您可以通过浏览器直接访问，无需下载任何软件，阅读进度会自动同步。' },
                { q: '漫画多久更新一次？', a: '热门连载漫画通常每周更新1-2次，具体更新频率取决于原作者的发布计划。我们会在最新章节发布后第一时间同步上线。' },
                { q: '如何联系51漫画的客服？', a: '您可以通过本页的联系表单、客服电话400-888-5151或发送邮件至contact@51comic.com与我们取得联系，我们会在24小时内回复。' },
                { q: '我是漫画作者，可以在51漫画发布作品吗？', a: '当然可以！我们非常欢迎原创漫画作者加入。请通过商务合作渠道联系我们，我们会有专人与您对接。' },
              ].map((faq, idx) => (
                <details key={idx} className="bg-background-50 rounded-lg border border-background-200/70 group">
                  <summary className="px-5 py-4 cursor-pointer flex items-center justify-between text-sm font-medium text-foreground-900">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "关于51漫画",
            description: "51漫画是一个免费在线漫画阅读平台，提供海量热门漫画作品，每日更新，高清画质，免费畅读。",
            url: "https://www.51comic.com/about",
            isPartOf: {
              "@type": "WebSite",
              name: "51漫画",
              url: "https://www.51comic.com",
            },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "首页", item: "https://www.51comic.com" },
                { "@type": "ListItem", position: 2, name: "关于我们" },
              ],
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              { "@type": "Question", "name": "51漫画是免费的吗？", "acceptedAnswer": { "@type": "Answer", "text": "是的，51漫画平台上的所有漫画作品均免费提供给读者在线阅读，无需付费。" } },
              { "@type": "Question", "name": "51漫画支持哪些设备？", "acceptedAnswer": { "@type": "Answer", "text": "51漫画支持电脑、平板、手机等多种设备。您可以通过浏览器直接访问，阅读进度会自动同步。" } },
              { "@type": "Question", "name": "漫画多久更新一次？", "acceptedAnswer": { "@type": "Answer", "text": "热门连载漫画通常每周更新1-2次，具体更新频率取决于原作者的发布计划。" } },
              { "@type": "Question", "name": "如何联系51漫画的客服？", "acceptedAnswer": { "@type": "Answer", "text": "您可以通过联系表单、客服电话400-888-5151或发送邮件至contact@51comic.com与我们取得联系。" } },
              { "@type": "Question", "name": "我是漫画作者，可以在51漫画发布作品吗？", "acceptedAnswer": { "@type": "Answer", "text": "欢迎原创漫画作者加入。请通过商务合作渠道联系我们，我们会有专人与您对接。" } },
            ],
          }),
        }}
      />
    </>
  );
}